import React from 'react';
import './TermsOfUse.css';

const TermsOfUse: React.FC = () => {
  return (
    <div className="terms-container">
      <div className="terms-content">
        <h1>Terms of Use</h1>

        <section>
          <h2>1. Agreement to Terms</h2>
          <p>
            These Terms of Use ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") 
            and CypressEra ("we," "us," or "our") concerning your access to and use of our power flow
            analysis software and related services (collectively, the "Services"). By accessing or using the Services, 
            you agree to be bound by these Terms. If you do not agree to these Terms, you must not access or use the Services.
          </p>
        </section>

        <section>
          <h2>2. Description of Services</h2>
          <p>
            CypressEra provides power system analysis software designed to assist engineers, researchers, and organizations 
            in analyzing electrical power systems. Our Services include, but are not limited to:
          </p>
          <ul>
            <li>Power flow calculation and simulation</li>
            <li>Optimal power flow solutions</li>
            <li>Contingency analysis</li>
            <li>Visualization and reporting tools</li>
            <li>Cloud-based computational services</li>
          </ul>
        </section>

        <section>
          <h2>3. User Accounts and Registration</h2>
          <h3>3.1 Account Creation</h3>
          <p>
            To access certain features of the Services, you may be required to create an account. You agree to:
          </p>
          <ul>
            <li>Provide accurate, current, and complete information during registration</li>
            <li>Maintain and promptly update your account information</li>
            <li>Maintain the security of your password and accept all risks of unauthorized access</li>
            <li>Notify us immediately if you discover any unauthorized use of your account</li>
          </ul>
          
          <h3>3.2 Account Responsibilities</h3>
          <p>
            You are responsible for all activities that occur under your account. We reserve the right to suspend or 
            terminate your account if we suspect any unauthorized use or violation of these Terms.
          </p>
        </section>

        <section>
          <h2>4. License Grant and Restrictions</h2>
          <h3>4.1 License Grant</h3>
          <p>
            Subject to your compliance with these Terms and payment of applicable fees, CypressEra grants you a 
            limited, non-exclusive, non-transferable, revocable license to access and use the Services for your 
            internal business or personal purposes during the subscription term.
          </p>
          
          <h3>4.2 Restrictions</h3>
          <p>You agree not to:</p>
          <ul>
            <li>Copy, modify, create derivative works of, distribute, or license the Services</li>
            <li>Reverse engineer, decompile, or disassemble any portion of the Services</li>
            <li>Rent, lease, or lend the Services to third parties</li>
            <li>Use the Services for any unlawful purpose or in violation of any laws</li>
            <li>Remove, alter, or obscure any proprietary notices on the Services</li>
            <li>Use the Services to develop a competing product or service</li>
            <li>Interfere with or disrupt the integrity or performance of the Services</li>
            <li>Attempt to gain unauthorized access to any portion of the Services</li>
          </ul>
        </section>

        <section>
          <h2>5. Intellectual Property Rights</h2>
          <p>
            The Services, including all software, algorithms, methodologies, documentation, and related materials, 
            are the exclusive property of CypressEra and its licensors. All rights, title, and interest in and to 
            the Services remain with CypressEra. These Terms do not grant you any ownership rights in the Services.
          </p>
          <p>
            Any feedback, suggestions, or improvements you provide regarding the Services shall be deemed non-confidential 
            and CypressEra shall have the right to use such feedback without restriction or compensation.
          </p>
        </section>

        <section>
          <h2>6. User Data and Content</h2>
          <h3>6.1 Your Data</h3>
          <p>
            You retain ownership of all data, models, and information you upload or input into the Services ("User Data"). 
            You grant CypressEra a limited license to use, process, and store User Data solely for the purpose of 
            providing the Services to you.
          </p>
          
          <h3>6.2 Data Security</h3>
          <p>
            We implement reasonable security measures to protect User Data. However, no method of transmission over the 
            Internet or electronic storage is completely secure. We cannot guarantee absolute security of User Data.
          </p>
          
          <h3>6.3 Acceptable Use</h3>
          <p>You agree not to use the Services to:</p>
          <ul>
            <li>Store or transmit malicious code, viruses, or harmful content</li>
            <li>Infringe upon the intellectual property rights of others</li>
            <li>Violate any applicable laws or regulations</li>
            <li>Transmit any content that is unlawful, defamatory, or obscene</li>
          </ul>
        </section>

        <section>
          <h2>7. Payment Terms</h2>
          <h3>7.1 Fees and Billing</h3>
          <p>
            You agree to pay all fees associated with your use of the Services. Fees are billed in advance on a 
            monthly or annual basis, as selected during registration. All payments are non-refundable except as 
            expressly provided in these Terms.
          </p>
          
          <h3>7.2 Price Changes</h3>
          <p>
            We may modify our pricing at any time. Price changes will be communicated to you at least 30 days in 
            advance. Your continued use of the Services after a price change becomes effective constitutes your 
            acceptance of the new pricing.
          </p>
          
          <h3>7.3 Taxes</h3>
          <p>
            You are responsible for all applicable taxes associated with your use of the Services, excluding taxes 
            based on CypressEra's net income.
          </p>
        </section>

        <section>
          <h2>8. Service Level and Availability</h2>
          <p>
            We strive to maintain high availability of the Services but do not guarantee uninterrupted access. 
            Scheduled maintenance will be communicated in advance when possible. We reserve the right to modify, 
            suspend, or discontinue any aspect of the Services at any time without liability.
          </p>
        </section>

        <section>
          <h2>9. Disclaimers</h2>
          <h3>9.1 "As Is" Basis</h3>
          <p>
            THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR 
            IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR 
            PURPOSE, NON-INFRINGEMENT, AND ACCURACY OF RESULTS.
          </p>
          
          <h3>9.2 No Guarantee of Results</h3>
          <p>
            While we strive to provide accurate power system analysis, we do not guarantee the accuracy, completeness, 
            or reliability of any results obtained through the Services. Users should independently verify all results 
            before making critical decisions based on such results.
          </p>
          
          <h3>9.3 Professional Judgment</h3>
          <p>
            The Services are intended to assist professionals in power system analysis but do not replace professional 
            engineering judgment. Users are responsible for interpreting results and making final decisions.
          </p>
        </section>

        <section>
          <h2>10. Limitation of Liability</h2>
          <h3>10.1 Exclusion of Consequential Damages</h3>
          <p>
            IN NO EVENT SHALL CYPRESSERA, ITS PRINCIPALS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR ANY
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF 
            PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OF THE 
            SERVICES, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY.
          </p>
          
          <h3>10.2 Cap on Liability</h3>
          <p>
            OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATED TO THE SERVICES SHALL NOT EXCEED THE GREATER 
            OF: (A) THE AMOUNT YOU PAID FOR THE SERVICES IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE 
            HUNDRED CANADIAN DOLLARS (CAD $100).
          </p>
        </section>

        <section>
          <h2>11. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless CypressEra and its principals, employees,
            agents, and affiliates from and against any claims, damages, losses, liabilities, and expenses 
            (including reasonable attorneys' fees) arising out of or relating to:
          </p>
          <ul>
            <li>Your use of the Services</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any third-party rights</li>
            <li>Any User Data you submit to the Services</li>
          </ul>
        </section>

        <section>
          <h2>12. Term and Termination</h2>
          <h3>12.1 Term</h3>
          <p>
            These Terms remain in effect until terminated by either party. You may terminate your account at any time 
            by contacting us or through your account settings.
          </p>
          
          <h3>12.2 Termination for Cause</h3>
          <p>
            We may suspend or terminate your access to the Services immediately, without notice, if you violate these 
            Terms or for any other reason at our sole discretion.
          </p>
          
          <h3>12.3 Effect of Termination</h3>
          <p>
            Upon termination, your right to use the Services will immediately cease. We may delete your account and 
            User Data following termination. Provisions of these Terms that by their nature should survive termination 
            shall survive, including intellectual property, disclaimers, limitation of liability, and indemnification.
          </p>
        </section>

        <section>
          <h2>13. Export Compliance</h2>
          <p>
            You agree not to export, re-export, or transfer the Services or any technical data in violation of 
            applicable export laws and regulations, including those of Canada and the United States. You represent 
            that you are not located in a country subject to a U.S. Government embargo or designated as a supporter 
            of terrorism.
          </p>
        </section>

        <section>
          <h2>14. Modifications to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify you of material changes by posting 
            the updated Terms on our website or by email. Your continued use of the Services after such modifications 
            constitutes your acceptance of the revised Terms. If you do not agree to the modified Terms, you must 
            discontinue use of the Services.
          </p>
        </section>

        <section>
          <h2>15. Governing Law and Dispute Resolution</h2>
          <h3>15.1 Governing Law</h3>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the Province of British 
            Columbia and the federal laws of Canada, without regard to conflict of law principles.
          </p>
          
          <h3>15.2 Dispute Resolution</h3>
          <p>
            Any dispute arising out of or relating to these Terms shall first be attempted to be resolved through 
            good faith negotiation. If negotiation fails, the dispute shall be resolved by binding arbitration 
            administered by the British Columbia International Commercial Arbitration Centre in accordance with its 
            rules. The arbitration shall take place in Vancouver, British Columbia.
          </p>
          
          <h3>15.3 Class Action Waiver</h3>
          <p>
            You agree not to participate in class actions, class arbitrations, or representative actions against 
            CypressEra. Any dispute resolution proceedings will be conducted only on an individual basis.
          </p>
        </section>

        <section>
          <h2>16. General Provisions</h2>
          <h3>16.1 Entire Agreement</h3>
          <p>
            These Terms, together with our Privacy Policy, constitute the entire agreement between you and CypressEra 
            concerning the Services and supersede all prior agreements and understandings.
          </p>
          
          <h3>16.2 Severability</h3>
          <p>
            If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in 
            full force and effect.
          </p>
          
          <h3>16.3 Waiver</h3>
          <p>
            Our failure to enforce any right or provision of these Terms will not be considered a waiver of such 
            right or provision.
          </p>
          
          <h3>16.4 Assignment</h3>
          <p>
            You may not assign or transfer these Terms without our prior written consent. We may assign these Terms 
            without restriction.
          </p>
          
          <h3>16.5 No Third-Party Beneficiaries</h3>
          <p>
            These Terms do not confer any benefits upon any third party unless expressly stated.
          </p>
        </section>

      </div>
    </div>
  );
};

export default TermsOfUse;
